<script setup lang="ts">

import { ref, watch } from "vue";
import { Header, SpecialFeatureList, OfferList } from "../Components";
import { useViewModelInstance as useCarbucksViewModelInstance} from "@app/Carbucks";
import { useViewModelInstance } from "../Types/MarketplaceViewModelStore";
import { useAppConfiguration } from "@domain/AppConfiguration";
import { ListStyle } from "../Types/ListStyle";

import FilterMenu from "../Components/Header/FilterMenu.vue";
import CarbucksPromotion from "../Components/Promotions/CarbucksPromotion.vue";
import OffersNearMePanel from "../Components/Common/OffersNearMePanel.vue";
import LocationNotAvailable from "../Components/Common/LocationNotAvailable.vue";

// Get the view model
const model = await useViewModelInstance();
const carbucks = await useCarbucksViewModelInstance();
await model.initialise();
const app = useAppConfiguration();

const hasLinkedCard = ( carbucks.profile.linkedCards?.length ?? 0 ) > 0;
const showPromotionPage = ref( hasLinkedCard == false );
const scrollContainer = ref<HTMLElement>();

// When the panel changes make sure the scroll container is scrolled back to the top
watch( () => model.activePanel, panel => {
    if( scrollContainer.value == null ) return;
    scrollContainer.value.scrollTop = 0;
});

</script>

<template>

    <div class="flex flex-col gap-y-8 3xl:gap-y-10 text-base 3xl:text-lg h-full"
         :disabled="model.isLoading">

        <Header v-model="model" :carbucks="carbucks" />

        <div ref="scrollContainer"
             class="h-full overflow-y-auto -mt-[5.5rem] 3xl:-mt-20 pt-20 pb-16">
            <TabPanels v-model="model.activePanel"
                       disabled>

                <!-- MARKETPLACE HOME PAGE -->
                <TabPanel value="marketplace" 
                          class="w-full min-h-full flex flex-col gap-y-8 3xl:gap-y-10 pb-16">
                    
                    <!-- CARBUCKS PROMOTION IF THIS IS THE FIRST VISIT AND THERE ARE NO LINKED CARDS -->
                    <CarbucksPromotion 
                        v-if="showPromotionPage" 
                        v-model="carbucks.profile"
                    />

                    <!-- SPECIAL FEATURED OFFERS -->
                    <SpecialFeatureList 
                        v-if="model.specialFeatureOffers.offers.length > 0"
                        v-model="model" 
                        class="-mb-6"
                        title="Offers you won't want to miss"
                        sub-title="The most popular offers in the community" 
                    />
                    
                    <!-- FEATURED OFFERS -->
                    <OfferList v-if="model.featureOffers.offers.length > 0"
                               v-model="model" 
                               :list="model.featureOffers.offers"
                               :list-style="ListStyle.HorizontalScroll" 
                               title="Featured Offers"
                               sub-title="Don't miss these great offers from our merchants" />

                    <!-- ORIGINATOR / DEALER OFFERS -->
                    <OfferList v-if="model.originatorOffers.offers.length > 0"
                               v-model="model" 
                               :list="model.originatorOffers.offers"
                               :list-style="ListStyle.HorizontalScroll" 
                               :title="`From ${app.name}`"
                               sub-title="Exclusive rewards from your home dealership" />

                    <!-- LATEST OFFERS -->
                    <OfferList v-if="model.latestOffers.offers.length > 0"
                               v-model="model" 
                               :list="model.latestOffers.offers"
                               :list-style="ListStyle.HorizontalScroll" 
                               title="Recently Added Offers"
                               sub-title="Fresh offers, newly added to the marketplace" />

                    <!-- OFFERS ENDING SOON -->
                    <OfferList v-if="model.offersEndingSoon.offers.length > 0"
                               v-model="model" 
                               :list="model.offersEndingSoon.offers"
                               :list-style="ListStyle.HorizontalScroll" 
                               title="Offers Ending Soon"
                               sub-title="Hurry, these offers are ending soon" />

                    <!-- OFFERS NEAR ME -->
                    <OffersNearMePanel v-model="model" />
                    
                    <!-- GIFT CARD OFFERS -->
                    <OfferList v-if="model.giftCardOffers.offers.length > 0"
                               v-model="model" 
                               :list="model.giftCardOffers.offers"
                               :list-style="ListStyle.HorizontalScroll" 
                               title="Gift Cards"
                               sub-title="Gifts for your special someone (or for yourself)" />
                </TabPanel>

                <!-- CATEGORY LIST PAGE -->
                <TabPanel value="category" 
                          class="w-full">
                    <OfferList v-model="model" 
                               :list="model.activeResults"
                               :enable-filter="true"
                               :list-style="ListStyle.StackedOneColumn" />
                </TabPanel>

                <!-- SEARCH RESULTS PAGE -->
                <TabPanel value="search" 
                          class="w-full">
                    <OfferList v-model="model" 
                               :list="model.activeResults"
                               :enable-filter="true"
                               :list-style="ListStyle.StackedTwoColumn" />
                </TabPanel>

                <!-- OFFERS NEAR ME PAGE -->
                <TabPanel value="nearest" 
                          class="w-full">
                    <OfferList v-if="model.hasSearchBoundary"
                               v-model="model" 
                               :list="model.activeResults"
                               :enable-filter="true"
                               :list-style="ListStyle.StackedOneColumn" />
                    <LocationNotAvailable v-else />
                </TabPanel>

            </TabPanels>
        </div>

        <FilterMenu v-model="model" />

    </div>
    
</template>